import { useEffect, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { useAuth } from "@/modules/growing-circle/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarHeart, Video, Check } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = { individual: "Individual", small_group: "Small group", thematic: "Thematic", community: "Community" };

export default function Meetings() {
  const { isListener } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => api.get("/meetings").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async (m) => {
    try {
      const { data } = await api.post(`/meetings/${m.id}/register`);
      toast.success(data.registered ? "You're registered for this meeting." : "Registration cancelled.");
      load();
    } catch {}
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" }); } catch { return iso; }
  };

  return (
    <div>
      <p className="eyebrow mb-4">Meetings</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">Live moments</h1>
      <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed mb-12">
        After sharing in writing, some experiences can be explored together, gently, through online meetings.
      </p>

      {items.length === 0 ? (
        <div className="bg-card border border-border rounded-3xl p-14 text-center">
          <CalendarHeart className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="font-heading text-2xl mb-2">No meetings scheduled</p>
          <p className="text-muted-foreground">Upcoming gatherings will appear here.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="meetings-list">
          {items.map((m) => (
            <div key={m.id} className="bg-card border border-border rounded-3xl p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div>
                <span className="text-xs uppercase tracking-widest text-accent">{KIND_LABEL[m.kind] || m.kind}</span>
                <h3 className="font-heading text-2xl mt-1 mb-1">{m.title}</h3>
                <p className="text-muted-foreground text-sm mb-2">{m.description}</p>
                <p className="text-sm text-foreground">{fmt(m.starts_at)}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {m.registered && m.zoom_link && (
                  <a href={m.zoom_link} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1">
                    <Video className="w-4 h-4" /> Join
                  </a>
                )}
                <Button variant={m.registered ? "outline" : "default"} className={`rounded-full ${!m.registered ? "btn-gold text-sm font-medium" : ""}`} onClick={() => register(m)} data-testid={`register-meeting-${m.id}`}>
                  {m.registered ? (<><Check className="w-4 h-4 mr-1" /> Registered</>) : "Attend"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
